import React, { useMemo } from "react";
import useSWR from "swr";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { useDashboard } from "@/pages/Dashboard";
import KPICard from "@/components/KPICard";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip,
  Legend, CartesianGrid, Cell,
} from "recharts";
import { cn } from "@/lib/utils";

const fetcher = (url) => api.get(url).then((r) => r.data);

const SLUG_TITLES = {
  "revenue-sales": { title: "Revenue & Sales", eyebrow: "Category" },
  "customer": { title: "Customer", eyebrow: "Category" },
  "membership-voucher": { title: "Membership & Voucher", eyebrow: "Category" },
  "operational": { title: "Operational", eyebrow: "Category" },
  "appointments": { title: "Appointments & Lost Business", eyebrow: "Category" },
  "cost-profitability": { title: "Cost & Profitability", eyebrow: "Category" },
  "benchmarking": { title: "Branch Benchmarking", eyebrow: "Category" },
};

const CHART_COLORS = ["#3C4A3E", "#C5A059", "#5B7A5F", "#8FA88C", "#2B362D", "#A94442", "#D4AF37", "#556B57"];

function fmtValue(v, unit) {
  if (v == null || Number.isNaN(v)) return "—";
  if (unit === "currency") {
    if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
    if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(2)} L`;
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(v);
  }
  if (unit === "percent") return `${v.toFixed(1)}%`;
  if (unit === "ratio") return v.toFixed(2);
  if (unit === "count") return new Intl.NumberFormat("en-IN").format(Math.round(v));
  if (unit === "hours") return `${v.toFixed(0)} hrs`;
  return v.toLocaleString();
}

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function DashboardCategory() {
  const { slug } = useParams();
  const { month, period, branchCodes, cities, ownership, docTypes } = useDashboard() || {};
  const meta = SLUG_TITLES[slug];

  const qs = new URLSearchParams();
  if (month) qs.set("month", month);
  if (period && period !== "month") qs.set("period", period);
  if (branchCodes?.length) qs.set("branches", branchCodes.join(","));
  if (cities?.length) qs.set("cities", cities.join(","));
  if (ownership?.length) qs.set("ownership", ownership.join(","));
  if (docTypes?.length) qs.set("doc_types", docTypes.join(","));
  const { data, isLoading } = useSWR(
    month ? `/api/dashboard/category/${slug}?${qs.toString()}` : null,
    fetcher
  );

  if (!meta) {
    return <div className="py-8 text-stone-500 font-mono text-sm">unknown category</div>;
  }

  if (isLoading || !data) {
    return <div className="py-8 text-stone-500 font-mono text-sm">loading category…</div>;
  }

  return (
    <div className="pt-6">
      <div className="mb-6">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-stone-500">
          {meta.eyebrow}
        </div>
        <h2 className="font-heading text-2xl font-medium tracking-tight text-stone-900 mt-1">
          {meta.title}
        </h2>
      </div>

      {/* KPI Cards */}
      {data.metrics?.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8" data-testid="category-kpi-grid">
          {data.metrics.map((m, i) => <KPICard key={m.metric_id} metric={m} index={i} />)}
        </div>
      )}

      {/* MoM + YoY dual chart panel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        <MoMChart metrics={data.metrics} periodKind={data.period?.kind || "month"} />
        <YoYChart metrics={data.metrics} years={data.years} />
      </div>

      {/* Dimension breakdowns */}
      <DimensionPanel category={data.category} dimensions={data.dimensions || {}} month={data.month || month} period={period} slug={slug} />
    </div>
  );
}

const TREND_TITLES = {
  month: ["Month over Month", "Last 12 months"],
  quarter: ["Quarter over Quarter", "Quarter by quarter"],
  ytd: ["Year to Date", "Cumulative, month by month"],
};

function MoMChart({ metrics, periodKind = "month" }) {
  const [selectedId, setSelectedId] = React.useState(metrics?.[0]?.metric_id);
  const m = metrics?.find((x) => x.metric_id === selectedId) || metrics?.[0];
  const chartData = (m?.trend || []).map((t) => ({
    month: t.label || t.month.slice(2), value: t.value ?? null,
  }));
  const [eyebrow, title] = TREND_TITLES[periodKind] || TREND_TITLES.month;
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-5" data-testid="mom-panel">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">{eyebrow}</div>
          <div className="text-sm font-heading font-medium text-stone-900 mt-0.5">{title}</div>
        </div>
        <select
          value={selectedId || ""}
          onChange={(e) => setSelectedId(e.target.value)}
          className="h-8 text-xs border border-stone-300 rounded-md bg-white px-2"
          data-testid="mom-metric-select"
        >
          {metrics?.map((m) => <option key={m.metric_id} value={m.metric_id}>{m.name}</option>)}
        </select>
      </div>
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e3db" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#5C5F5C" }} axisLine={{ stroke: "#e5e3db" }} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: "#5C5F5C" }} axisLine={{ stroke: "#e5e3db" }} tickLine={false}
                   tickFormatter={(v) => Math.abs(v) >= 1000 ? `${(v/1000).toFixed(0)}k` : v.toString()} />
            <Tooltip
              contentStyle={{ backgroundColor: "#fff", border: "1px solid #e5e3db", borderRadius: 6, fontSize: 12 }}
              formatter={(v) => fmtValue(v, m?.unit)}
            />
            <Line type="monotone" dataKey="value" stroke="#3C4A3E" strokeWidth={2} dot={{ r: 3, fill: "#3C4A3E" }} activeDot={{ r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function YoYChart({ metrics, years }) {
  const [selectedId, setSelectedId] = React.useState(metrics?.[0]?.metric_id);
  const m = metrics?.find((x) => x.metric_id === selectedId) || metrics?.[0];
  // Filter years with at least one non-null value for this metric
  const yearsWithData = useMemo(() => {
    if (!m || !years) return [];
    return years.filter((y) => (m.yoy || []).some((r) => r[String(y)] != null && r[String(y)] !== 0));
  }, [m, years]);
  const chartData = (m?.yoy || []).map((r) => {
    const row = { month: MONTH_ABBR[r.month_num - 1] };
    for (const y of yearsWithData) row[String(y)] = r[String(y)];
    return row;
  });
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-5" data-testid="yoy-panel">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">Year over Year</div>
          <div className="text-sm font-heading font-medium text-stone-900 mt-0.5">
            Same months across years
          </div>
        </div>
        <select
          value={selectedId || ""}
          onChange={(e) => setSelectedId(e.target.value)}
          className="h-8 text-xs border border-stone-300 rounded-md bg-white px-2"
          data-testid="yoy-metric-select"
        >
          {metrics?.map((m) => <option key={m.metric_id} value={m.metric_id}>{m.name}</option>)}
        </select>
      </div>
      <div className="h-64 w-full">
        {yearsWithData.length === 0 ? (
          <div className="h-full grid place-items-center text-xs font-mono text-stone-400">
            Only one year of data — YoY comparison will populate after next year&apos;s uploads
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e3db" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#5C5F5C" }} axisLine={{ stroke: "#e5e3db" }} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#5C5F5C" }} axisLine={{ stroke: "#e5e3db" }} tickLine={false}
                     tickFormatter={(v) => Math.abs(v) >= 1000 ? `${(v/1000).toFixed(0)}k` : v.toString()} />
              <Tooltip
                contentStyle={{ backgroundColor: "#fff", border: "1px solid #e5e3db", borderRadius: 6, fontSize: 12 }}
                formatter={(v) => fmtValue(v, m?.unit)}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
              {yearsWithData.map((y, idx) => (
                <Bar key={y} dataKey={String(y)} fill={CHART_COLORS[idx % CHART_COLORS.length]} radius={[2, 2, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function DimensionPanel({ category, dimensions, month, period, slug }) {
  if (!dimensions || Object.keys(dimensions).length === 0) return null;

  return (
    <div className="space-y-6" data-testid="dimension-panel">
      {dimensions.doc_type_mix && (
        <DimensionCard
          title="Revenue by Doc Type"
          subtitle="How the invoice mix breaks down (SS=Standard Service, GS=Gift-Card Service, GCS=Gift Card Sold, GCC=Gift Card Cash, PS=Prepaid, OS=Other)"
          rows={dimensions.doc_type_mix}
          labelKey="doc_type"
          valueKey="current"
          priorKey="prior"
          unit="currency"
        />
      )}
      {dimensions.payment_mix && (
        <DimensionCard
          title="Revenue by Payment Mode"
          subtitle="Where the money comes from"
          rows={dimensions.payment_mix}
          labelKey="mode"
          valueKey="current"
          priorKey="prior"
          unit="currency"
        />
      )}
      {dimensions.city_mix && (
        <DimensionCard
          title="Revenue by City"
          subtitle="Geographic split across outlets"
          rows={dimensions.city_mix}
          labelKey="city"
          valueKey="current"
          priorKey="prior"
          unit="currency"
        />
      )}
      {dimensions.customer_split && (
        <DimensionCard
          title="Customer Split"
          subtitle="New vs Repeat vs Members"
          rows={dimensions.customer_split}
          labelKey="label"
          valueKey="current"
          priorKey="prior"
          unit="count"
        />
      )}
      {dimensions.prepaid_flow && (
        <DimensionCard
          title="Prepaid & Voucher Flow"
          subtitle="What flowed in and out this period"
          rows={dimensions.prepaid_flow}
          labelKey="label"
          valueKey="current"
          priorKey="prior"
          unit="currency"
        />
      )}
      {dimensions.appointment_status && (
        <DimensionCard
          title="Appointment Outcomes"
          subtitle="Where booked slots ended up"
          rows={dimensions.appointment_status}
          labelKey="label"
          valueKey="current"
          priorKey="prior"
          unit="count"
        />
      )}
      {dimensions.hours_split && (
        <DimensionCard
          title="Hours Breakdown"
          subtitle="Attendance vs delivery vs capacity"
          rows={dimensions.hours_split}
          labelKey="label"
          valueKey="current"
          priorKey="prior"
          unit="hours"
        />
      )}
      {dimensions.cost_split && slug === "cost-profitability" && (
        <PnLTable month={month} period={period} />
      )}
      {dimensions.cost_split && slug !== "cost-profitability" && (
        <DimensionCard
          title="Revenue vs Direct Cost"
          subtitle="What's left after paying the operators"
          rows={dimensions.cost_split}
          labelKey="label"
          valueKey="current"
          priorKey="prior"
          unit="currency"
        />
      )}
      {dimensions.branch_table && (
        <BranchLeagueTables rows={dimensions.branch_table} />
      )}
      {dimensions.card_type_mix && (
        <CardTypeCard rows={dimensions.card_type_mix} />
      )}
      {dimensions.operator_leaderboard && (
        <OperatorLeaderboard rows={dimensions.operator_leaderboard} />
      )}
    </div>
  );
}

function DimensionCard({ title, subtitle, rows, labelKey, valueKey, priorKey, unit }) {
  const total = rows.reduce((s, r) => s + (r[valueKey] || 0), 0);
  const chartRows = rows.filter((r) => (r[valueKey] || 0) !== 0);
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-5" data-testid={`dimension-${title.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="mb-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">Dimension</div>
        <div className="text-sm font-heading font-medium text-stone-900 mt-0.5">{title}</div>
        <div className="text-xs text-stone-500 mt-1">{subtitle}</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1.4fr] gap-6">
        {/* Table */}
        <div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-bold uppercase tracking-[0.15em] text-stone-500 border-b border-stone-200">
                <th className="py-2">Slice</th>
                <th className="py-2 text-right">Current</th>
                <th className="py-2 text-right">Share</th>
                <th className="py-2 text-right">Prior</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const share = total > 0 ? (r[valueKey] / total) * 100 : 0;
                const delta = (r[valueKey] || 0) - (r[priorKey] || 0);
                return (
                  <tr key={i} className="border-b border-stone-100 last:border-0">
                    <td className="py-2 font-mono text-xs">
                      <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                      {r[labelKey]}
                    </td>
                    <td className="py-2 text-right font-mono text-xs">{fmtValue(r[valueKey], unit)}</td>
                    <td className="py-2 text-right font-mono text-xs text-stone-500">{share.toFixed(1)}%</td>
                    <td className="py-2 text-right font-mono text-xs text-stone-500">{fmtValue(r[priorKey], unit)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Bar */}
        <div className="h-56">
          {chartRows.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartRows} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e3db" vertical={false} />
                <XAxis dataKey={labelKey} tick={{ fontSize: 10, fill: "#5C5F5C" }} axisLine={{ stroke: "#e5e3db" }} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#5C5F5C" }} axisLine={{ stroke: "#e5e3db" }} tickLine={false}
                       tickFormatter={(v) => Math.abs(v) >= 1000 ? `${(v/1000).toFixed(0)}k` : v.toString()} />
                <Tooltip
                  contentStyle={{ backgroundColor: "#fff", border: "1px solid #e5e3db", borderRadius: 6, fontSize: 12 }}
                  formatter={(v) => fmtValue(v, unit)}
                />
                <Bar dataKey={valueKey} radius={[2, 2, 0, 0]}>
                  {chartRows.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full grid place-items-center text-xs font-mono text-stone-400">No data for this period</div>
          )}
        </div>
      </div>
    </div>
  );
}

function BranchLeagueTables({ rows }) {
  const coco = rows.filter(r => (r.ownership_type || "").toLowerCase() === "coco");
  const franchise = rows.filter(r => (r.ownership_type || "").toLowerCase() === "franchise");
  return (
    <div className="space-y-6" data-testid="branch-league-tables">
      <BranchTable rows={rows} eyebrow="All outlets" title="Branch league table"
        testId="branch-table" rowPrefix="branch-row" />
      <BranchTable rows={coco} eyebrow={`COCO outlets · ${coco.filter(r => r.branch_code !== HO_CODE).length}`} title="COCO branch league"
        testId="branch-table-coco" rowPrefix="branch-row-coco" />
      <BranchTable rows={franchise} eyebrow={`Franchise outlets · ${franchise.length}`} title="Franchise branch league"
        testId="branch-table-franchise" rowPrefix="branch-row-franchise" />
    </div>
  );
}

// Head Office is shown first and is not ranked: it carries corporate sales
// (bulk vouchers / memberships, GST invoices), not an outlet's business.
const HO_CODE = "HO";

function BranchTable({ rows, eyebrow = "All outlets", title = "Branch league table",
                       testId = "branch-table", rowPrefix = "branch-row" }) {
  const ordered = [
    ...rows.filter(r => r.branch_code === HO_CODE),
    ...rows.filter(r => r.branch_code !== HO_CODE),
  ];
  let rank = 1;
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-5" data-testid={testId}>
      <div className="mb-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">{eyebrow}</div>
        <div className="text-sm font-heading font-medium text-stone-900 mt-0.5">{title}</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] font-bold uppercase tracking-[0.15em] text-stone-500 border-b border-stone-200">
              <th className="py-2 pr-3">#</th>
              <th className="py-2 pr-3">Branch</th>
              <th className="py-2 pr-3">City</th>
              <th className="py-2 pr-3 text-right">Revenue</th>
              <th className="py-2 pr-3 text-right">Prior</th>
              <th className="py-2 pr-3 text-right">Δ %</th>
              <th className="py-2 pr-3 text-right">Invoices</th>
              <th className="py-2 pr-3 text-right">Target</th>
              <th className="py-2 pr-3 text-right">Ach %</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={9} className="py-3 text-xs text-stone-400">no branches in this group</td></tr>
            )}
            {ordered.map((r) => {
              const isHO = r.branch_code === HO_CODE;
              const delta = r.revenue_prior ? ((r.revenue - r.revenue_prior) / Math.abs(r.revenue_prior)) * 100 : null;
              const achievement = r.revenue_target ? (r.revenue / r.revenue_target) * 100 : null;
              return (
                <tr key={r.branch_code}
                    className={cn("border-b border-stone-100 last:border-0", isHO && "bg-stone-50 border-b-stone-300")}
                    data-testid={`${rowPrefix}-${r.branch_code}`}>
                  <td className="py-2 pr-3 font-mono text-xs text-stone-500">{isHO ? "—" : rank++}</td>
                  <td className="py-2 pr-3 font-medium">{r.branch_name}</td>
                  <td className="py-2 pr-3 font-mono text-xs text-stone-500">{r.city}</td>
                  <td className="py-2 pr-3 text-right font-mono text-xs">{fmtValue(r.revenue, "currency")}</td>
                  <td className="py-2 pr-3 text-right font-mono text-xs text-stone-500">{fmtValue(r.revenue_prior, "currency")}</td>
                  <td className={cn("py-2 pr-3 text-right font-mono text-xs", delta == null ? "text-stone-500" : delta >= 0 ? "text-brand-good" : "text-brand-bad")}>
                    {delta == null ? "—" : `${delta >= 0 ? "+" : ""}${delta.toFixed(1)}%`}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono text-xs">{r.invoice_count?.toLocaleString?.() ?? "—"}</td>
                  <td className="py-2 pr-3 text-right font-mono text-xs text-stone-500">{fmtValue(r.revenue_target, "currency")}</td>
                  <td className={cn("py-2 pr-3 text-right font-mono text-xs", achievement == null ? "text-stone-500" : achievement >= 100 ? "text-brand-good" : "text-brand-bad")}>
                    {achievement == null ? "—" : `${achievement.toFixed(0)}%`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CardTypeCard({ rows }) {
  const CARD_LABELS = {
    SIL: "Silver", CRY: "Crystal", GOL: "Gold",
    DIA: "Diamond", PLA: "Platinum", PRIVI: "Privilege", RUPAY: "RuPay",
  };
  const chartRows = rows.filter((r) => (r.current || 0) > 0);
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-5" data-testid="card-type-panel">
      <div className="mb-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">Dimension</div>
        <div className="text-sm font-heading font-medium text-stone-900 mt-0.5">
          Redemption by Membership Tier
        </div>
        <div className="text-xs text-stone-500 mt-1">Which tier is actually drawing on their balance</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1.4fr] gap-6">
        <div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-bold uppercase tracking-[0.15em] text-stone-500 border-b border-stone-200">
                <th className="py-2">Tier</th>
                <th className="py-2 text-right">Redeem</th>
                <th className="py-2 text-right">Balance</th>
                <th className="py-2 text-right">Members</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.card_type} className="border-b border-stone-100 last:border-0" data-testid={`tier-row-${r.card_type}`}>
                  <td className="py-2 font-mono text-xs">
                    <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                    <span className="font-medium">{r.card_type}</span>
                    <span className="text-stone-500 ml-1">{CARD_LABELS[r.card_type] ? `· ${CARD_LABELS[r.card_type]}` : ""}</span>
                  </td>
                  <td className="py-2 text-right font-mono text-xs">{fmtValue(r.current, "currency")}</td>
                  <td className="py-2 text-right font-mono text-xs text-stone-500">{fmtValue(r.balance, "currency")}</td>
                  <td className="py-2 text-right font-mono text-xs text-stone-500">{r.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="h-56">
          {chartRows.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartRows} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e3db" vertical={false} />
                <XAxis dataKey="card_type" tick={{ fontSize: 10, fill: "#5C5F5C" }} axisLine={{ stroke: "#e5e3db" }} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#5C5F5C" }} axisLine={{ stroke: "#e5e3db" }} tickLine={false}
                       tickFormatter={(v) => Math.abs(v) >= 1000 ? `${(v/1000).toFixed(0)}k` : v.toString()} />
                <Tooltip
                  contentStyle={{ backgroundColor: "#fff", border: "1px solid #e5e3db", borderRadius: 6, fontSize: 12 }}
                  formatter={(v) => fmtValue(v, "currency")}
                />
                <Bar dataKey="current" radius={[2, 2, 0, 0]}>
                  {chartRows.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full grid place-items-center text-xs font-mono text-stone-400">No tier redemption this period</div>
          )}
        </div>
      </div>
    </div>
  );
}

function OperatorLeaderboard({ rows }) {
  const max = rows.reduce((m, r) => Math.max(m, r.revenue || 0), 0);
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-5" data-testid="operator-leaderboard">
      <div className="mb-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">Dimension</div>
        <div className="text-sm font-heading font-medium text-stone-900 mt-0.5">Top 20 Operators by Revenue</div>
        <div className="text-xs text-stone-500 mt-1">Stars, coaching gaps, and revenue-per-hour outliers</div>
      </div>
      {rows.length === 0 ? (
        <div className="py-8 text-center text-xs font-mono text-stone-400">No operator data for this period</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-bold uppercase tracking-[0.15em] text-stone-500 border-b border-stone-200">
                <th className="py-2 pr-3">#</th>
                <th className="py-2 pr-3">Operator</th>
                <th className="py-2 pr-3">Branches</th>
                <th className="py-2 pr-3">Revenue</th>
                <th className="py-2 pr-3 text-right">Hours</th>
                <th className="py-2 pr-3 text-right">₹/Hr</th>
                <th className="py-2 pr-3 text-right">Services</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const pct = max > 0 ? (r.revenue / max) * 100 : 0;
                return (
                  <tr key={r.operator + i} className="border-b border-stone-100 last:border-0" data-testid={`op-row-${i}`}>
                    <td className="py-2 pr-3 font-mono text-xs text-stone-500">{i + 1}</td>
                    <td className="py-2 pr-3 font-medium">{r.operator}</td>
                    <td className="py-2 pr-3 font-mono text-[10px] text-stone-500">{r.branches.slice(0, 3).join(", ")}{r.branches.length > 3 ? ` +${r.branches.length - 3}` : ""}</td>
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-stone-100 rounded-full overflow-hidden min-w-[80px]">
                          <div className="h-full bg-brand rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="font-mono text-xs whitespace-nowrap">{fmtValue(r.revenue, "currency")}</span>
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-right font-mono text-xs text-stone-500">{r.hours.toFixed(0)}</td>
                    <td className="py-2 pr-3 text-right font-mono text-xs">{fmtValue(r.revenue_per_hour, "currency")}</td>
                    <td className="py-2 pr-3 text-right font-mono text-xs text-stone-500">{r.services}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


/* -------------------------------------------------------------------- */
/*                              P&L TABLE                                */
/* -------------------------------------------------------------------- */
function fmtCur(n) {
  if (n == null || Number.isNaN(n)) return "—";
  const neg = n < 0;
  const a = Math.abs(n);
  const s = a >= 10000000
    ? `₹${(a / 10000000).toFixed(2)} Cr`
    : a >= 100000
    ? `₹${(a / 100000).toFixed(2)} L`
    : `₹${a.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  return neg ? `-${s}` : s;
}

function fmtPct(v) {
  if (v == null || Number.isNaN(v)) return "—";
  return `${v.toFixed(1)}%`;
}

function PnLTable({ month, period }) {
  const { branchCodes, cities, ownership } = useDashboard() || {};
  const [mode, setMode] = React.useState("single");
  const [settingsOpen, setSettingsOpen] = React.useState(false);

  // Single mode = ownership split (ALL / COCO / Franchise). Global filters
  // scope the ALL column only; COCO & Franchise always cover their group.
  const splitQs = new URLSearchParams({ month });
  if (branchCodes?.length) splitQs.set("branches", branchCodes.join(","));
  if (cities?.length) splitQs.set("cities", cities.join(","));
  if (ownership?.length) splitQs.set("ownership", ownership.join(","));
  const allQs = new URLSearchParams({ month, mode: "all" });
  if (period && period !== "month") {
    splitQs.set("period", period);
    allQs.set("period", period);
  }

  const { data: splitData, mutate: mutateSplit } = useSWR(
    month && mode === "single" ? `/api/pnl/ownership-split?${splitQs.toString()}` : null, fetcher);
  const { data: allData, mutate: mutateAll } = useSWR(
    month && mode === "all" ? `/api/pnl?${allQs.toString()}` : null, fetcher);

  return (
    <div className="col-span-full rounded-lg border border-stone-200 bg-white p-5" data-testid="pnl-table">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">Dimension</div>
          <div className="text-sm font-heading font-medium text-stone-900 mt-0.5">Branch-wise Profit &amp; Loss</div>
          <div className="text-xs text-stone-500 mt-1">
            Bank-statement based P&amp;L — Revenue from DSR, costs from Cost Others (Withdrawal ÷ Category).
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-stone-200 bg-white overflow-hidden text-[10px] font-bold uppercase tracking-[0.15em]">
            <button
              onClick={() => setMode("single")}
              className={cn("px-3 py-1", mode === "single" ? "bg-stone-900 text-white" : "text-stone-600")}
              data-testid="pnl-mode-single"
            >ALL / COCO / Franchise</button>
            <button
              onClick={() => setMode("all")}
              className={cn("px-3 py-1 border-l border-stone-200", mode === "all" ? "bg-stone-900 text-white" : "text-stone-600")}
              data-testid="pnl-mode-all"
            >All branches</button>
          </div>
          <button
            onClick={() => setSettingsOpen(true)}
            className="text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1 rounded-md border border-stone-200 hover:border-brand hover:text-brand"
            data-testid="pnl-categorize-btn"
          >⚙ Categorize</button>
        </div>
      </div>

      {mode === "single" && splitData && <PnLOwnershipSplit data={splitData} />}
      {mode === "all" && allData?.rows && <PnLGroupedAll rows={allData.rows} />}
      {((mode === "single" && !splitData) || (mode === "all" && !allData)) &&
        <div className="text-xs text-stone-400 py-4">loading…</div>}

      <CategorySettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onChanged={() => { mutateSplit && mutateSplit(); mutateAll && mutateAll(); }}
      />
    </div>
  );
}

const SPLIT_COLS = [
  { key: "all", label: "ALL" },
  { key: "coco", label: "COCO" },
  { key: "franchise", label: "Franchise" },
];

function bucketMapFor(row, name) {
  if (!row) return {};
  if (name === "indirect") {
    const merged = { ...(row.operating || {}) };
    Object.entries(row.non_operating || {}).forEach(([k, v]) => { merged[k] = (merged[k] || 0) + v; });
    return merged;
  }
  return row[name] || {};
}

function PnLOwnershipSplit({ data }) {
  const [expanded, setExpanded] = React.useState({ direct: false, indirect: false });
  const toggle = (b) => setExpanded(s => ({ ...s, [b]: !s[b] }));
  const rows = { all: data.all, coco: data.coco, franchise: data.franchise };
  const counts = data.counts || {};

  const catsFor = (bucket) => {
    const set = new Set();
    SPLIT_COLS.forEach(c => Object.keys(bucketMapFor(rows[c.key], bucket)).forEach(k => set.add(k)));
    return [...set].sort();
  };
  const anyUncat = SPLIT_COLS.some(c => (rows[c.key]?.uncategorized_total || 0) > 0);

  return (
    <div className="border border-stone-200 rounded-md overflow-x-auto">
      <table className="w-full text-sm min-w-[520px]" data-testid="pnl-ownership-split-table">
        <thead className="bg-stone-50 text-[10px] font-bold uppercase tracking-[0.15em] text-stone-500">
          <tr>
            <th className="text-left px-4 py-2">Line</th>
            {SPLIT_COLS.map(c => (
              <th key={c.key} className="text-right px-4 py-2" data-testid={`pnl-split-col-${c.key}`}>
                {c.label}
                {counts[c.key] != null && <span className="block font-normal normal-case text-[9px] text-stone-400">{counts[c.key]} br</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {PNL_LINES.map(line => {
            const isBucket = !!line.bucket;
            const isOpen = isBucket && expanded[line.bucket];
            const catList = isBucket ? catsFor(line.bucket) : [];
            return (
              <React.Fragment key={line.key}>
                <tr
                  className={cn(
                    "border-t border-stone-100",
                    line.indent && "bg-stone-50/40",
                    isBucket && catList.length > 0 && "cursor-pointer hover:bg-stone-100/60",
                  )}
                  onClick={isBucket && catList.length > 0 ? () => toggle(line.bucket) : undefined}
                  data-testid={isBucket ? `pnl-split-bucket-${line.bucket}` : undefined}
                >
                  <td className={cn("px-4 py-2", line.className)}>
                    {isBucket && catList.length > 0 && (
                      <span className="inline-block w-3 text-stone-400 text-[10px] mr-1">{isOpen ? "▾" : "▸"}</span>
                    )}
                    {line.label}
                  </td>
                  {SPLIT_COLS.map(c => {
                    const row = rows[c.key] || {};
                    return (
                      <td key={c.key} className={cn("px-4 py-2 text-right font-mono", line.className)}
                          data-testid={`pnl-split-${line.key}-${c.key}`}>
                        {line.pct ? fmtPct(row[line.key]) : fmtCur(row[line.key])}
                      </td>
                    );
                  })}
                </tr>
                {isOpen && catList.map(cat => (
                  <tr key={`${line.bucket}-${cat}`} className="bg-stone-50/70" data-testid={`pnl-split-cat-${line.bucket}-${cat}`}>
                    <td className="px-4 py-1.5 pl-10 text-xs text-stone-600">{cat}</td>
                    {SPLIT_COLS.map(c => {
                      const m = bucketMapFor(rows[c.key], line.bucket);
                      return <td key={c.key} className="px-4 py-1.5 text-right font-mono text-xs text-stone-600">{fmtCur(m[cat] || 0)}</td>;
                    })}
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          {anyUncat && (
            <tr className="border-t border-amber-300 bg-amber-50">
              <td className="px-4 py-2 text-xs text-amber-800">⚠ Uncategorized costs (not in any bucket)</td>
              {SPLIT_COLS.map(c => (
                <td key={c.key} className="px-4 py-2 text-right font-mono text-xs text-amber-800">
                  {fmtCur(rows[c.key]?.uncategorized_total || 0)}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

const PNL_LINES = [
  { key: "revenue",             label: "Revenue (Gross)",              className: "font-semibold" },
  { key: "direct_total",        label: "Less: Direct Costs",           className: "text-stone-600", indent: true, bucket: "direct" },
  { key: "contribution",        label: "Contribution / Gross Margin",  className: "font-bold border-t border-stone-200 pt-2" },
  { key: "gross_margin_pct",    label: "Gross Margin %",               className: "text-stone-500 italic", pct: true },
  { key: "indirect_total",      label: "Less: Indirect Expenses",      className: "text-stone-600", indent: true, bucket: "indirect" },
  { key: "ebitda",              label: "EBITDA",                       className: "font-bold border-t border-stone-200 pt-2" },
  { key: "ebitda_pct",          label: "EBITDA %",                     className: "text-stone-500 italic", pct: true },
  // Capex — shown for reference below EBITDA, never part of it
  { key: "investing_outflow_total", label: "Investing Cash Outflow",   className: "text-stone-600 border-t border-stone-200", indent: true, bucket: "investing_outflow" },
  { key: "investing_inflow_total",  label: "Investing Cash Inflow",    className: "text-stone-600", indent: true, bucket: "investing_inflow" },
];

const BUCKET_LABELS = {
  non_operating: "Non-Op",
  investing_outflow: "Invest Out",
  investing_inflow: "Invest In",
};

function PnLSingle({ row, branchLabel }) {
  const [expanded, setExpanded] = React.useState({ direct: false, indirect: false });
  const toggle = (b) => setExpanded(s => ({ ...s, [b]: !s[b] }));

  // "indirect" is a virtual bucket = operating ∪ non_operating (all costs below Gross Margin)
  const bucketRows = (name) => {
    if (name === "indirect") {
      const merged = { ...(row.operating || {}), ...(row.non_operating || {}) };
      // If a category name collides between buckets, sum them
      Object.entries(row.non_operating || {}).forEach(([k, v]) => {
        if ((row.operating || {})[k] != null) merged[k] = (row.operating[k] || 0) + v;
      });
      return Object.entries(merged).sort((a, b) => b[1] - a[1]);
    }
    return Object.entries(row[name] || {}).sort((a, b) => b[1] - a[1]);
  };

  return (
    <div className="border border-stone-200 rounded-md overflow-hidden">
      <table className="w-full text-sm" data-testid="pnl-single-table">
        <thead className="bg-stone-50 text-[10px] font-bold uppercase tracking-[0.15em] text-stone-500">
          <tr><th className="text-left px-4 py-2">Line</th><th className="text-right px-4 py-2">{branchLabel}</th></tr>
        </thead>
        <tbody>
          {PNL_LINES.map(line => {
            const isBucket = !!line.bucket;
            const isOpen = isBucket && expanded[line.bucket];
            const catRows = isBucket ? bucketRows(line.bucket) : [];
            return (
              <React.Fragment key={line.key}>
                <tr
                  className={cn(
                    "border-t border-stone-100",
                    line.indent && "bg-stone-50/40",
                    isBucket && catRows.length > 0 && "cursor-pointer hover:bg-stone-100/60",
                  )}
                  onClick={isBucket && catRows.length > 0 ? () => toggle(line.bucket) : undefined}
                  data-testid={isBucket ? `pnl-bucket-${line.bucket}` : undefined}
                >
                  <td className={cn("px-4 py-2", line.className)}>
                    {isBucket && catRows.length > 0 && (
                      <span className="inline-block w-3 text-stone-400 text-[10px] mr-1">
                        {isOpen ? "▾" : "▸"}
                      </span>
                    )}
                    {line.label}
                  </td>
                  <td className={cn("px-4 py-2 text-right font-mono", line.className)}>
                    {line.pct ? fmtPct(row[line.key]) : fmtCur(row[line.key])}
                  </td>
                </tr>
                {isOpen && catRows.map(([cat, val]) => (
                  <tr key={`${line.bucket}-${cat}`} className="bg-stone-50/70" data-testid={`pnl-cat-${line.bucket}-${cat}`}>
                    <td className="px-4 py-1.5 pl-10 text-xs text-stone-600">{cat}</td>
                    <td className="px-4 py-1.5 text-right font-mono text-xs text-stone-600">{fmtCur(val)}</td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          {row.uncategorized_total > 0 && (
            <tr className="border-t border-amber-300 bg-amber-50">
              <td className="px-4 py-2 text-xs text-amber-800">⚠ Uncategorized costs (not in any bucket)</td>
              <td className="px-4 py-2 text-right font-mono text-xs text-amber-800">{fmtCur(row.uncategorized_total)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function PnLGroupedAll({ rows }) {
  const groups = [
    { key: "COCO", label: "COCO" },
    { key: "Franchise", label: "Franchise" },
  ];
  const byType = (t) => rows.filter(r => (r.ownership_type || "").toLowerCase() === t.toLowerCase());
  const untagged = rows.filter(r => !["coco", "franchise"].includes((r.ownership_type || "").toLowerCase()));

  return (
    <div className="space-y-6" data-testid="pnl-grouped-all">
      {groups.map(g => {
        const subset = byType(g.key);
        return (
          <div key={g.key} data-testid={`pnl-all-group-${g.key.toLowerCase()}`}>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">
              {g.label}
              <span className="text-stone-400 normal-case tracking-normal"> — {subset.length} branch{subset.length === 1 ? "" : "es"}</span>
            </div>
            <PnLAll rows={subset} />
          </div>
        );
      })}
      {untagged.length > 0 && (
        <div data-testid="pnl-all-group-other">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">
            Other<span className="text-stone-400 normal-case tracking-normal"> — {untagged.length} branches</span>
          </div>
          <PnLAll rows={untagged} />
        </div>
      )}
    </div>
  );
}

function PnLAll({ rows }) {
  const [expanded, setExpanded] = React.useState({ direct: false, indirect: false });
  const toggle = (b) => setExpanded(s => ({ ...s, [b]: !s[b] }));

  if (rows.length === 0) return <div className="text-xs text-stone-400 py-4">no branches with data for this period</div>;

  // Union of all categories seen across branches for each bucket. "indirect" merges operating + non_operating.
  const sortedCats = {};
  PNL_LINES.filter(l => l.bucket).forEach(l => {
    const set = new Set();
    rows.forEach(r => Object.keys(bucketMapFor(r, l.bucket)).forEach(c => set.add(c)));
    sortedCats[l.bucket] = [...set].sort();
  });
  const catValue = (r, bucket, cat) => bucketMapFor(r, bucket)[cat] || 0;

  return (
    <div className="border border-stone-200 rounded-md overflow-x-auto">
      <table className="text-sm min-w-full" data-testid="pnl-all-table">
        <thead className="bg-stone-50 text-[10px] font-bold uppercase tracking-[0.15em] text-stone-500">
          <tr>
            <th className="text-left px-3 py-2 sticky left-0 bg-stone-50 z-10">Line</th>
            {rows.map(r => (
              <th key={r.branch_code} className="text-right px-3 py-2 whitespace-nowrap">
                {r.branch_code}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {PNL_LINES.map(line => {
            const isBucket = !!line.bucket;
            const isOpen = isBucket && expanded[line.bucket];
            const cats = isBucket ? sortedCats[line.bucket] : [];
            return (
              <React.Fragment key={line.key}>
                <tr
                  className={cn(
                    "border-t border-stone-100",
                    line.indent && "bg-stone-50/40",
                    isBucket && cats.length > 0 && "cursor-pointer hover:bg-stone-100/60",
                  )}
                  onClick={isBucket && cats.length > 0 ? () => toggle(line.bucket) : undefined}
                  data-testid={isBucket ? `pnl-all-bucket-${line.bucket}` : undefined}
                >
                  <td className={cn("px-3 py-2 sticky left-0 bg-white z-10", line.className)}>
                    {isBucket && cats.length > 0 && (
                      <span className="inline-block w-3 text-stone-400 text-[10px] mr-1">
                        {isOpen ? "▾" : "▸"}
                      </span>
                    )}
                    {line.label}
                  </td>
                  {rows.map(r => (
                    <td key={r.branch_code} className={cn("px-3 py-2 text-right font-mono whitespace-nowrap", line.className)}>
                      {line.pct ? fmtPct(r[line.key]) : fmtCur(r[line.key])}
                    </td>
                  ))}
                </tr>
                {isOpen && cats.map(cat => (
                  <tr key={`${line.bucket}-${cat}`} className="bg-stone-50/70" data-testid={`pnl-all-cat-${line.bucket}-${cat}`}>
                    <td className="px-3 py-1.5 pl-8 sticky left-0 bg-stone-50/70 z-10 text-xs text-stone-600 whitespace-nowrap">{cat}</td>
                    {rows.map(r => (
                      <td key={r.branch_code} className="px-3 py-1.5 text-right font-mono text-xs text-stone-600 whitespace-nowrap">
                        {fmtCur(catValue(r, line.bucket, cat))}
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CategorySettingsDialog({ open, onClose, onChanged }) {
  const { data, mutate } = useSWR(open ? "/api/pnl/categories" : null, fetcher);
  if (!open) return null;
  const cats = data?.categories || [];
  const buckets = data?.buckets || ["direct", "operating", "non_operating", "investing_outflow", "investing_inflow"];
  const uncategorized = cats.filter(c => !c.bucket);
  const categorized = cats.filter(c => c.bucket);

  const setBucket = async (cat, bucket) => {
    try {
      await api.patch(`/api/pnl/categories/${encodeURIComponent(cat)}`, { bucket });
      mutate();
      onChanged?.();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="fixed inset-0 bg-stone-900/40 flex items-center justify-center z-50 p-4" onClick={onClose}
         data-testid="category-settings-overlay">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={e=>e.stopPropagation()}>
        <div className="p-5 border-b border-stone-200 flex items-center justify-between">
          <div>
            <div className="font-heading text-lg font-medium">Categorize Cost Others</div>
            <div className="text-xs text-stone-500">Assign each bank-statement category to a P&amp;L bucket. Uncategorized costs are shown as a warning line in the P&amp;L. Investing (capex) buckets are shown below EBITDA and are not deducted from it — Invest Out uses withdrawals, Invest In uses deposits.</div>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-900">✕</button>
        </div>
        <div className="p-5 space-y-6">
          {uncategorized.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700 mb-2">
                Needs mapping · {uncategorized.length}
              </div>
              {uncategorized.map(c => <CategoryRow key={c.category} c={c} buckets={buckets} onSet={setBucket} />)}
            </div>
          )}
          {categorized.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">
                Mapped · {categorized.length}
              </div>
              {categorized.map(c => <CategoryRow key={c.category} c={c} buckets={buckets} onSet={setBucket} />)}
            </div>
          )}
          {cats.length === 0 && (
            <div className="text-xs text-stone-400">No categories discovered yet — upload a Cost Others sheet with a Category column.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function CategoryRow({ c, buckets, onSet }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{c.category}</div>
        <div className="text-[10px] text-stone-500 font-mono">
          {c.occurrence_count} txns · {fmtCur(c.bucket === "investing_inflow" ? c.total_inflow : c.total_cost)}
          {c.bucket === "investing_inflow" && " deposits"}
        </div>
      </div>
      <div className="inline-flex rounded-md border border-stone-200 overflow-hidden text-[10px] font-bold uppercase tracking-[0.15em]" data-testid={`cat-row-${c.category}`}>
        {buckets.map(b => (
          <button
            key={b}
            onClick={() => onSet(c.category, b)}
            className={cn("px-2 py-1 border-l border-stone-200 first:border-l-0",
              c.bucket === b ? "bg-stone-900 text-white" : "text-stone-500 hover:bg-stone-50")}
            data-testid={`cat-btn-${c.category}-${b}`}
          >
            {BUCKET_LABELS[b] || b}
          </button>
        ))}
        <button
          onClick={() => onSet(c.category, null)}
          className={cn("px-2 py-1 border-l border-stone-200",
            !c.bucket ? "bg-amber-100 text-amber-900" : "text-stone-400 hover:bg-stone-50")}
          data-testid={`cat-btn-${c.category}-clear`}
        >
          ↺
        </button>
      </div>
    </div>
  );
}
